import {Autocomplete, AutocompleteValue, CircularProgress, TextField,} from "@mui/material";
import * as React from "react";
import {useEffect, useState} from "react";
import {useDebounce} from "@/hooks/use-debounce";
import {FilterOptionsState} from "@mui/material/useAutocomplete/useAutocomplete";

interface AutocompleteInputProps<T, Multiple extends boolean | undefined> {
  onChange: (value: AutocompleteValue<T, Multiple, false, false>) => void;
  value?: AutocompleteValue<T, Multiple, false, false>;
  fetchOptions: (words: string) => Promise<void>;
  minWordLength?: number;
  multiple?: Multiple;
  getOptionLabel?: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  minWidth?: string;
  disabled?: boolean;
  options: T[];
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & { key: any },
    option: T,
  ) => React.ReactNode;
  loading?: boolean;
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

// TODO: Fix A props object containing a "key" prop is being spread into JSX
export const AutocompleteInput = <T, Multiple extends boolean | undefined>({
                                                                             minWordLength = 1,
                                                                             getOptionLabel,
                                                                             minWidth = "200px",
                                                                             onChange,
                                                                             value,
                                                                             fetchOptions,
                                                                             isOptionEqualToValue,
                                                                             options,
                                                                             loading,
                                                                             multiple,
                                                                             disabled,
                                                                             renderOption,
                                                                             filterOptions,
                                                                             inputRef,
                                                                             placeholder
                                                                           }: AutocompleteInputProps<T, Multiple>) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  
  const debounce = useDebounce(300);
  
  const [internalLoading, setInternalLoading] = useState(false);
  
  const handleInputChange = (words: string) => {
    setInputValue(words);
    if (words.length < minWordLength) {
      return;
    }
    setInternalLoading(true);
    debounce(async () => {
      await fetchOptions(words);
      setInternalLoading(false);
    });
  };
  
  useEffect(
    () => handleInputChange(""),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );
  
  // https://github.com/mui/material-ui/pull/39795
  return (
    <Autocomplete
      renderOption={renderOption}
      disabled={disabled}
      sx={{minWidth: minWidth}}
      getOptionLabel={getOptionLabel}
      options={options}
      multiple={multiple}
      open={open}
      loading={loading || internalLoading}
      inputValue={inputValue}
      filterOptions={filterOptions}
      onChange={(_e, v) => {
        onChange(v);
        if (!multiple) setOpen(false);
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "selectOption") {
          return;
        }
        if (reason === "reset") {
          setInputValue(newInputValue);
          return;
        }
        newInputValue.length >= minWordLength ? setOpen(true) : setOpen(false);
        handleInputChange(newInputValue);
      }}
      isOptionEqualToValue={isOptionEqualToValue}
      noOptionsText="検索結果がありません"
      onBlur={() => setOpen(false)}
      onFocus={() =>setOpen(true)}
      value={value}
      renderInput={(params) => (
        <TextField
          placeholder={placeholder}
          inputRef={inputRef}
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20}/>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          size={"small"}
        />
      )}
    />
  );
};
